/**
 * EditorialHeroMetric — the signature "hero numeral" treatment: a big SERIF
 * display value, a small MONO unit riding its baseline, and a wide-tracked
 * mono small-caps LABEL above. This is the redesign's 48.5 MW / $4-7M /
 * 300 MW / 2.92 km motif, re-skinned to OUR theme tokens (NOT the warm-amber
 * skin) so it reads on-brand in every preset.
 *
 * Reference: `~/Desktop/Redhead_BTM_Feasibility_Redesign_1.pdf` — the
 * "69 KV SUBSTATION / $4-7M" row and the panel hero "FIRM-DELIVERABLE 48.5 MW":
 *   label  → mono, uppercase, 0.14em tracking, muted/role ink
 *   value  → SERIF display numeral (inherits the section serif; mono is NOT
 *            forced — the serif is the craft lift over the existing mono rail)
 *   unit   → small mono, muted, sits after the numeral
 *
 * Tone-aware: `tone` retints BOTH the value and (optionally) the label to a
 * status role (positive / warning / negative / accent). On a dark
 * instrument-panel the same primitive is used with `surface='dark'`, which
 * routes every color through `onInstrument()` so it reads on the deep surface
 * (see EditorialInstrumentPanel).
 *
 * Theme tokens read (light): 'ink' (value), 'muted' (label + unit), or the
 * status role for a toned value/label. Dark surface routes the same roles
 * through `onInstrument()`. Serif value via the inherited section font; mono
 * label/unit via --report-body-font.
 *
 * Pure / print-safe (no shadow, no animation). A null/undefined/'' `value`
 * renders nothing (returns null) so callers can compose hero rails that never
 * fabricate a number — matching EditorialKpiRail / EditorialDatasheet.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor, onInstrument, type ReportColorRole } from '../../../../lib/reportRoleColors';

const MONO = 'var(--report-body-font, inherit)';
/**
 * The display numeral uses the section's own heading face — the report root
 * (viewer SiteRagReportView + PDF SiteRagPrintExport) both set `--heading-font`
 * from `theme.headingFont`, so this is print-safe in both render paths. Falls
 * back to the body font, then inherit, when the var is absent (e.g. a unit
 * test mount with no report root).
 */
const SERIF = 'var(--heading-font, var(--report-body-font, inherit))';

export type EditorialHeroTone = 'default' | 'positive' | 'warning' | 'negative' | 'accent';
export type EditorialHeroSize = 'sm' | 'md' | 'lg';
export type EditorialHeroSurface = 'light' | 'dark';

export interface EditorialHeroMetricProps {
  theme: ReportTheme;
  /** Wide-tracked mono small-caps label, e.g. "Firm-Deliverable (ELCC)". */
  label: React.ReactNode;
  /** The serif display numeral, e.g. "48.5", "$4-7M", "300". '' drops it. */
  value: React.ReactNode;
  /** Small mono unit riding the numeral baseline, e.g. "MW", "km". */
  unit?: React.ReactNode;
  /** Optional faint sub-line under the value (mono, e.g. "Planning default"). */
  sub?: React.ReactNode;
  /** Retint the value (and label, if `toneLabel`) to a status role. */
  tone?: EditorialHeroTone;
  /** Also retint the LABEL to the tone (default keeps the label neutral). */
  toneLabel?: boolean;
  /** Display size. 'lg' is the panel hero; 'md' the default; 'sm' a chip. */
  size?: EditorialHeroSize;
  /** 'dark' routes colors through onInstrument() for the dark panel surface. */
  surface?: EditorialHeroSurface;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

const TONE_ROLE: Record<EditorialHeroTone, ReportColorRole> = {
  default: 'ink',
  positive: 'positive',
  warning: 'warning',
  negative: 'negative',
  accent: 'accent',
};

/** Serif numeral size per scale (the value is the visual anchor). */
const VALUE_REM: Record<EditorialHeroSize, number> = { sm: 1.5, md: 2, lg: 2.75 };
const UNIT_REM: Record<EditorialHeroSize, number> = { sm: 0.75, md: 0.8125, lg: 0.875 };
const LABEL_PX: Record<EditorialHeroSize, number> = { sm: 9.5, md: 10, lg: 11 };

function isRenderableValue(value: React.ReactNode): boolean {
  if (value == null || value === false) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

export function EditorialHeroMetric({
  theme,
  label,
  value,
  unit,
  sub,
  tone = 'default',
  toneLabel = false,
  size = 'md',
  surface = 'light',
  id,
  className,
  style,
}: EditorialHeroMetricProps): React.ReactElement | null {
  if (!isRenderableValue(value)) return null;

  // Route every color through the right resolver for the surface so the dark
  // panel never bakes a literal and the light variant follows the preset.
  const pick = (role: ReportColorRole): string =>
    surface === 'dark' ? onInstrument(theme, role) : roleColor(theme, role);

  const valueInk = pick(TONE_ROLE[tone]);
  const labelInk = toneLabel && tone !== 'default' ? pick(TONE_ROLE[tone]) : pick('muted');
  const unitInk = pick('muted');
  const subInk = pick('faint');

  return (
    // `data-editorial-keep` — house-style keep-whole hook (2026-06-29): the paged
    // deck targets this so a verdict / hero metric never splits label from value
    // across a page seam (see PaginatedReportView).
    <div id={id} className={className} role="group" data-editorial-keep="true" style={style}>
      {isRenderableValue(label) && (
        <div
          style={{
            fontFamily: MONO,
            fontSize: LABEL_PX[size],
            fontWeight: 500,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: labelInk,
            marginBottom: 4,
          }}
        >
          {label}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span
          style={{
            fontFamily: SERIF,
            fontSize: `${VALUE_REM[size]}rem`,
            lineHeight: 1,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: valueInk,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </span>
        {unit != null && (
          <span
            style={{
              fontFamily: MONO,
              fontSize: `${UNIT_REM[size]}rem`,
              fontWeight: 500,
              letterSpacing: '0.04em',
              color: unitInk,
            }}
          >
            {unit}
          </span>
        )}
      </div>
      {isRenderableValue(sub) && (
        <div
          style={{
            fontFamily: MONO,
            fontSize: size === 'lg' ? 10.5 : 9.5,
            letterSpacing: '0.04em',
            color: subInk,
            marginTop: 4,
            lineHeight: 1.4,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}
